import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { qk } from "../../lib/queryKeys";
import { getJson } from "../../lib/http";
import { useCompaniesStore } from "../../store/admin/companiesStore";
import { useVehiclesSocketStore } from "../../store/admin/vehiclesSocketStore";
import {
  useDriversStore,
  type AvailableDriver,
  type Driver,
} from "../../store/admin/driversStore";

export interface DriverListParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}

interface ListMeta {
  error: string | null;
  count: number;
  status: number;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  url: string;
}

export interface DriverListResult {
  data: Driver[];
  meta?: ListMeta;
}

export function useDriversList(params: DriverListParams = {}) {
  const companyId = useCompaniesStore((s) => s.companyId);
  return useQuery({
    queryKey: qk.drivers.list(companyId, params),
    queryFn: ({ signal }) =>
      getJson<DriverListResult>(
        "/driverList",
        { page: 1, limit: 6, ...params },
        { signal },
      ).then((body) => ({ data: body.data ?? [], meta: body.meta })),
  });
}

/**
 * Un conductor por id, para cuando sólo hace falta su ficha.
 *
 * La lista viene paginada de seis en seis y compartida con la pantalla de
 * administración: pedirla entera para sacar una foto traería decenas de
 * registros y pisaría la paginación de quien la esté usando. Esto es una
 * lectura puntual y cacheada.
 *
 * `/driver` devuelve el documento con distinto anidado según el camino, así que
 * se desenvuelve acá y no en cada pantalla.
 */
export function useDriver(driverId: string | null | undefined) {
  const companyId = useCompaniesStore((s) => s.companyId);

  return useQuery({
    queryKey: qk.drivers.one(companyId, driverId ?? null),
    enabled: Boolean(driverId),
    queryFn: ({ signal }) =>
      getJson<unknown>("/driver", { id: driverId as string }, { signal }).then((body) => {
        const b = body as { data?: { data?: Driver } | Driver };
        const anidado = (b?.data as { data?: Driver })?.data;
        return (anidado ?? (b?.data as Driver) ?? (body as Driver)) || null;
      }),
    // La foto y el nombre de un conductor no cambian en una sesión de
    // monitoreo; refrescarlos con cada render sería ruido de red.
    staleTime: 10 * 60 * 1000,
  });
}

function extractDrivers(body: unknown): AvailableDriver[] {
  const payload = (body as { data?: unknown })?.data ?? body;
  if (Array.isArray(payload)) return payload as AvailableDriver[];
  const nested = (payload as { data?: unknown })?.data;
  if (Array.isArray(nested)) return nested as AvailableDriver[];
  return [];
}

export function useAvailableDrivers() {
  const companyId = useCompaniesStore((s) => s.companyId);
  const selectedCompany = useVehiclesSocketStore((s) => s.selectedCompany);
  const cid = selectedCompany || companyId;

  const query = useQuery({
    queryKey: qk.availableDrivers(cid),
    queryFn: ({ signal }) =>
      getJson<unknown>(
        "/availableDrivers",
        { company_id: cid ?? "" },
        { signal },
        { headers: { "x-company-id": cid ?? "" } },
      ).then(extractDrivers),
    enabled: !!cid,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (query.data) {
      useDriversStore.setState({ availableDrivers: query.data });
    }
  }, [query.data]);

  return query;
}
